// src/components/appeals/AppealsTable.tsx

import React from 'react';
import { DataTable, type DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { Appeal, AppealGroup } from '../../types';

// Define a type for the data structure that the main component passes to the table
type AppealsData = Appeal[] | AppealGroup[];

/**
 * Props for the AppealsTable component.
 */
interface AppealsTableProps {
  columns: DataTableColumn<any>[];
  data: AppealsData;
  pagination: any;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  tableParams: any; // Use more specific type if possible, e.g., TableParams
  setParams: (params: any) => void;
  onRowClick: (row: any) => void;
  getRowClassName: (row: any) => string;
  rowKeyPrefix?: string;
}

/**
 * @description Renders the core DataTable component with common props.
 */
const AppealsTable: React.FC<AppealsTableProps> = ({
  columns,
  data,
  pagination,
  isLoading,
  error,
  onRetry,
  tableParams,
  setParams,
  onRowClick,
  getRowClassName,
  rowKeyPrefix = 'global',
}) => {
  return (
    <div className="py-4">
      <DataTable
        key={`table-${rowKeyPrefix}`}
        columns={columns}
        data={{
          data: data,
          total: pagination.total,
          page: pagination.page,
          limit: pagination.limit,
        }}
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        pagination={{
          limit: pagination?.limit ?? 3,
          page: pagination?.page ?? 1,
        }}
        onPaginationChange={newPagination => setParams({ ...tableParams, ...newPagination })}
        emptyState={{
          icon: 'HiClipboardList',
          title: 'No appeals data available',
          description: 'There are no appeals matching your current filters.',
        }}
        onSort={sortBy => setParams({ ...tableParams, sorts: { ...sortBy } })}
        sorting={tableParams.sorts}
        onRowClick={onRowClick}
        tableContainerClassName="overflow-y-auto relative"
        tableClassName="min-w-full divide-y divide-gray-200 relative"
        rowKey="invoiceId"
        stickyHeader={true}
        pageSizeOptions={[25, 50, 100, 200]}
        loadingRowCount={25}
        loadingColumnCount={6}
        getRowClassName={getRowClassName}
        enableColumnResizing
        columnWidthsStorageKey={`appeal-${rowKeyPrefix}`}
      />
    </div>
  );
};

export default AppealsTable;
